import { Box, Checkbox, Stack, Typography, useMediaQuery } from "@mui/material";
import {
  MultiChoiceAnswerV1,
  MultiChoiceAnswerV1DTO,
} from "@pulse/pulse-rpcs/lib/types/multiChoiceAnswerV1";
import { SurveySettingsEnums } from "@pulse/pulse-rpcs/lib/types/surveySettings";
import {
  EMPTY_JSON,
  RICH_TEXT_DEFAULT_FONT_COLOR,
  RICH_TEXT_MARKDOWN_PROPS,
  createMultiChoiceQuestionV1Model,
  createQuestionOptionsModel,
  richTextTypography,
} from "@pulse/shared-components";
import { MultiChoiceQuestionV1Model } from "@pulse/shared-components";
import {
  useCornerRadius,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { RichTextEditor } from "@surya-digital/leo-reactjs-remirror";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React, { useCallback, useEffect, useMemo } from "react";

const DESKTOP_BREAKPOINT = "(min-width:1200px)";

interface ChatMultiSelectQuestionBodyProps {
  questionDetailsJson: string;
  draftAnswerJson?: string;
  fontFamily: string;
  fontScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
  maxSelectionText: string;
  isMandatory: boolean;
  onAnswerChange: (answerJson: string | null, isValid: boolean) => void;
}

interface ChatMultiSelectAnswerListProps {
  questionDetailsJson: string;
  answerJson: string;
}

const isAnswerPresent = (answerJson: string): boolean =>
  answerJson !== EMPTY_JSON;

const createQuestionModel = (
  questionDetailsJson: string,
  answerJson?: string,
): Instance<typeof MultiChoiceQuestionV1Model> => {
  const model = createMultiChoiceQuestionV1Model(undefined, [
    createQuestionOptionsModel(),
  ]);
  model.deserializeQuestionJSON(questionDetailsJson);
  if (answerJson && isAnswerPresent(answerJson)) {
    const savedAnswer = JSON.parse(answerJson) as MultiChoiceAnswerV1DTO;
    model.deserializeAnswerJSON(
      JSON.stringify({
        ...savedAnswer,
        selectedChoices: savedAnswer.selectedChoices.map((choice) => ({
          ...choice,
          answer:
            model.options.find((option) => option.code === choice.code.code)
              ?.option ?? choice.answer,
        })),
      }),
    );
  }
  return model;
};

const ChatOptionText = ({
  text,
  fontFamily,
  fontScale,
  isBigScreen,
  color,
}: {
  text: string;
  fontFamily: string;
  fontScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
  isBigScreen: boolean;
  color: string;
}): React.ReactElement => (
  <Box
    sx={{
      color,
      minWidth: 0,
      "& .ProseMirror": { color, overflowWrap: "anywhere" },
    }}
  >
    <RichTextEditor
      name="chatMultiSelectOption"
      initialValue={text}
      typography={richTextTypography(
        fontFamily,
        fontScale,
        RICH_TEXT_DEFAULT_FONT_COLOR,
        isBigScreen,
      )}
      mode="preview"
      supports={RICH_TEXT_MARKDOWN_PROPS}
      borderOnPreview={false}
    />
  </Box>
);

/**
 * Chat-only multi-select interaction. The timeline owns the question bubble
 * and its submit CTA; this component holds the editable checkbox choices.
 */
export const ChatMultiSelectQuestionBody = observer(
  ({
    questionDetailsJson,
    draftAnswerJson,
    fontFamily,
    fontScale,
    maxSelectionText,
    isMandatory,
    onAnswerChange,
  }: ChatMultiSelectQuestionBodyProps): React.ReactElement => {
    const tokens = useFoundationColorTokens();
    const spacing = useSpacing();
    const radius = useCornerRadius();
    const typography = useTypography();
    const isBigScreen = useMediaQuery(DESKTOP_BREAKPOINT);
    const optionBorderColor = `var(--chat-group-option-border-color, ${tokens.border})`;
    const model = useMemo(
      () => createQuestionModel(questionDetailsJson, draftAnswerJson),
      [draftAnswerJson, questionDetailsJson],
    );

    const publishAnswer = useCallback((): void => {
      onAnswerChange(
        model.selectedOptions.length > 0 ? model.serializedAnswerJSON : null,
        !isMandatory || model.validateUserResponse === null,
      );
    }, [isMandatory, model, onAnswerChange]);

    useEffect(() => {
      publishAnswer();
    }, [publishAnswer]);

    const toggleOption = (code: string, option: string): void => {
      model.setSelectedOptions({ code, option });
      publishAnswer();
    };

    const shouldShowMaxSelection = model.options.length !== model.maxSelection;
    const helperText = shouldShowMaxSelection
      ? `${maxSelectionText} ${model.maxSelection}`.trim()
      : null;

    return (
      <Stack gap={spacing.spaceXS} width="100%">
        {helperText && (
          <Typography sx={{ ...typography.b2, color: tokens.labelSubtle }}>
            {helperText}
          </Typography>
        )}
        <Stack gap={spacing.spaceSM} width="100%">
          {model.options.map((option) => {
            const selected = model.isChoiceSelected({
              code: option.code,
              option: option.option,
            });
            return (
              <Stack
                key={option.code}
                component="div"
                role="checkbox"
                aria-checked={selected}
                tabIndex={0}
                direction="row"
                alignItems="center"
                gap={spacing.spaceXS}
                minHeight="44px"
                padding={`${spacing.spaceXS} ${spacing.spaceSM}`}
                bgcolor={tokens.backgroundElevatedLevel1}
                border={
                  selected
                    ? `2px solid ${tokens.borderPrimary}`
                    : `1px solid ${optionBorderColor}`
                }
                borderRadius={radius.radiusXS}
                textAlign="left"
                onClick={(): void => toggleOption(option.code, option.option)}
                onKeyDown={(event): void => {
                  if (event.key === " " || event.key === "Enter") {
                    event.preventDefault();
                    toggleOption(option.code, option.option);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  font: "inherit",
                  "&:hover": {
                    backgroundColor: selected
                      ? tokens.backgroundPrimarySubtle
                      : tokens.backgroundHovered,
                  },
                }}
              >
                <Checkbox
                  checked={selected}
                  disableRipple
                  inputProps={{ "aria-hidden": true, tabIndex: -1 }}
                  sx={{
                    padding: 0,
                    pointerEvents: "none",
                    color: tokens.iconPrimary,
                    "&.Mui-checked": { color: tokens.iconPrimary },
                  }}
                />
                <ChatOptionText
                  text={option.option}
                  fontFamily={fontFamily}
                  fontScale={fontScale}
                  isBigScreen={isBigScreen}
                  color={tokens.label}
                />
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    );
  },
);

/** Renders completed multi-select values as bullets, including one answer. */
export const ChatMultiSelectAnswerList = ({
  questionDetailsJson,
  answerJson,
}: ChatMultiSelectAnswerListProps): React.ReactElement => {
  const typography = useTypography();
  const model = useMemo(
    () => createQuestionModel(questionDetailsJson),
    [questionDetailsJson],
  );
  const answer = useMemo(
    () => MultiChoiceAnswerV1.fromDTO(JSON.parse(answerJson)),
    [answerJson],
  );

  return (
    <Box
      component="ul"
      sx={{ margin: 0, paddingInlineStart: "20px", overflowWrap: "anywhere" }}
    >
      {answer.selectedChoices.map((choice) => {
        const option = model.options.find(
          (configuredOption) => configuredOption.code === choice.code.code,
        );
        return (
          <Typography component="li" key={choice.code.code} sx={typography.b1}>
            {option?.option ?? choice.answer ?? choice.code.code}
          </Typography>
        );
      })}
    </Box>
  );
};
